<template>
  <div class="calculator-page">
    <header class="calculator-header">
      <h1>Калькулятор колец</h1>
      <p>Расчет массы кольца по его физическим параметрам</p>
    </header>
    <section class="calculator-section calculator-main">
      <div class="left-panel">
        <div class="calculator-input">
          <rings-calculator-input-selectbox
            v-bind:name="'metal-select'"
            v-bind:label="'Металл'"
            v-bind:options="Object.keys(density)"
            v-model="metal"
          ></rings-calculator-input-selectbox>
        </div>
        <div class="calculator-input">
          <rings-calculator-input-number
            v-bind:name="'size-input'"
            v-bind:label="'Размер'"
            v-bind:min="14"
            v-bind:max="24"
            v-bind:step="0.25"
            v-model.number="size"
          ></rings-calculator-input-number>
        </div>
      </div>
      <div class="right-panel">
        <rings-calculator-output-weight
          v-bind:weight="weight"
        ></rings-calculator-output-weight>
      </div>
    </section>
    <section class="calculator-section calculator-profile">
      <div class="left-panel">
        <div class="calculator-input">
          <rings-calculator-input-number
            v-bind:name="'width-input'"
            v-bind:label="'Ширина'"
            v-bind:milimeter="true"
            v-bind:min="0.5"
            v-bind:max="20"
            v-bind:step="0.1"
            v-model.number="width"
          ></rings-calculator-input-number>
        </div>
        <div class="calculator-input">
          <rings-calculator-input-number
            v-bind:name="'height-input'"
            v-bind:label="'Высота'"
            v-bind:milimeter="true"
            v-bind:min="0.2"
            v-bind:max="5"
            v-bind:step="0.1"
            v-model.number="height"
          ></rings-calculator-input-number>
        </div>
      </div>
      <div class="right-panel">
        <rings-calculator-output-profile
          v-bind:shape="profile"
        ></rings-calculator-output-profile>
        <div class="calculator-input">
          <rings-calculator-input-radio
            v-bind:name="'profile'"
            v-bind:value="profile"
            v-bind:label="'Профиль'"
            v-bind:values="['semicircular', 'rectangular']"
            v-bind:names="['Полукруглый', 'Прямоугольный']"
            v-model="profile"
          ></rings-calculator-input-radio>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import utils from './utils'
import RingsCalculatorInputNumber from '../components/the-content/RingsCalculatorInputNumber'
import RingsCalculatorInputSelectbox from '../components/the-content/RingsCalculatorInputSelectbox'
import RingsCalculatorInputRadio from '../components/the-content/RingsCalculatorInputRadio'
import RingsCalculatorOutputProfile from '../components/the-content/RingsCalculatorOutputProfile'
import RingsCalculatorOutputWeight from '../components/the-content/RingsCalculatorOutputWeight'

export default {
  name: 'RingsCalculatorPage',
  components: {
    RingsCalculatorInputNumber,
    RingsCalculatorInputSelectbox,
    RingsCalculatorInputRadio,
    RingsCalculatorOutputProfile,
    RingsCalculatorOutputWeight
  },
  data () {
    return {
      profile: 'semicircular',
      metal: 'Золото 585°',
      size: 18,
      width: 4,
      height: 1,
      density: {
        'Золото 375°': 0.01209,
        'Золото 585°': 0.01339,
        'Золото 750°': 0.01549,
        'Серебро 925°': 0.01039,
        'Платина': 0.02145
      }
    }
  },
  computed: {
    radius: function () {
      return this.size / 2
    },
    weight: function () {
      return this.volumeRing * this.density[this.metal]
    },
    volumeRing: function () {
      return (this.profile === 'semicircular')
        ? this.volumeSemicircularRing : this.volumeRectangularRing
    },
    volumeSemicircularRing: function () {
      const cornerRadius = this.height * 0.29
      const widthWoCorners = this.width - 2 * cornerRadius
      const torusTubeCenterDistance = (
        widthWoCorners ** 2 / (8 * (this.height - 2 * cornerRadius)) -
        this.height / 2
      )
      const torusTubeRadius = this.height + torusTubeCenterDistance
      const torusTubeCenterToCornerLine = torusTubeCenterDistance + cornerRadius
      const torusTubeCenterToCornerCenter = Math.sqrt(
        widthWoCorners ** 2 / 4 + torusTubeCenterToCornerLine ** 2
      )
      const sinPhi = widthWoCorners / (2 * torusTubeCenterToCornerCenter)
      const cosPhi = torusTubeCenterToCornerLine / torusTubeCenterToCornerCenter

      const semicircWidth = 2 * torusTubeRadius * sinPhi
      const baseHeight = torusTubeRadius * cosPhi - torusTubeCenterDistance
      const semicircHeight = this.height - baseHeight
      const semicircRadius = this.radius + baseHeight

      if ((semicircWidth < widthWoCorners) || (widthWoCorners < 0)) {
        alert('Ширина профиля слишком мала относительно его высоты')
      }

      const volumeSemicirc = this.volumeTorusCylinderDifference(
        semicircRadius, semicircHeight, semicircWidth)

      const volumeRect = this.volumeAnnularCylinder(
        this.radius, semicircRadius, widthWoCorners)

      const volumeInnerRoundings = this.volumeTorusCylinderIntersection(
        this.radius + cornerRadius, cornerRadius, 2 * cornerRadius)

      let volumeOuterRoundings = this.volumeTorusCylinderDifference(
        this.radius + cornerRadius, cornerRadius, 2 * cornerRadius)

      volumeOuterRoundings -= this.volumeTorusCylinderDifference(
        semicircRadius,
        2 * cornerRadius - baseHeight,
        semicircWidth - widthWoCorners)

      return (
        volumeSemicirc +
        volumeRect +
        volumeInnerRoundings +
        volumeOuterRoundings)
    },
    volumeRectangularRing: function () {
      const cornerRadius = Math.min(this.width, this.height) / 4
      const radiusOuter = this.radius + this.height

      let volumeWoRoundings = this.volumeAnnularCylinder(
        this.radius, radiusOuter, this.width - 2 * cornerRadius)

      volumeWoRoundings += 2 * this.volumeAnnularCylinder(
        this.radius + cornerRadius, radiusOuter - cornerRadius, cornerRadius)

      const volumeInnerRoundings = this.volumeTorusCylinderIntersection(
        this.radius + cornerRadius, cornerRadius, 2 * cornerRadius)

      const volumeOuterRoundings = this.volumeTorusCylinderDifference(
        radiusOuter - cornerRadius, cornerRadius, 2 * cornerRadius)

      return volumeWoRoundings + volumeInnerRoundings + volumeOuterRoundings
    }
  },
  metaInfo () {
    return {
      title: 'Калькулятор колец',
      meta: utils.getDefaultMetaTags({
        description:
          'Калькулятор для расчета массы колец по их физическим параметрам',
        path: this.$router.resolve(this.$route).href,
        title: 'Калькулятор колец'
      })
    }
  },
  mounted () {
    if (window['__PRERENDER_INJECTED']) {
      document.dispatchEvent(new Event('render-event'))
    }
  },
  methods: {
    volumeCylinder: function (radius, height) {
      return Math.PI * (radius ** 2) * height
    },
    volumeAnnularCylinder: function (radiusInner, radiusOuter, height) {
      const volumeInner = this.volumeCylinder(radiusInner, height)
      const volumeOuter = this.volumeCylinder(radiusOuter, height)
      return volumeOuter - volumeInner
    },
    volumeTorusCylinderIntersection (radiusCylinder, width, height) {
      // Finds the volume of the intersection of concentric torus and cylinder

      // Radius of the such torus tube that intersects the cylinder
      // section at its corner points and has a certain width of a segment at
      // the intersection of the torus tube (circle) and cylinder section
      // (rectangular).
      const radiusTorusTube = height ** 2 / (8 * width) + width / 2
      // The distance from the center of the torus to the center of its tube
      const radiusTorus = radiusCylinder - width + radiusTorusTube
      // Central angle of torus tube from which the intersection segment is seen
      const phi = 2 * Math.asin((height / (2 * radiusTorusTube)).toPrecision(5))

      return (
        this.volumeCylinder(radiusCylinder, height) -
        this.volumeCylinder(radiusTorus, height) -
        this.volumeCylinder(radiusTorusTube, height) +
        Math.PI * (height ** 3) / 12 +
        Math.PI * (radiusTorusTube ** 2) * radiusTorus * (phi + Math.sin(phi))
      )
    },
    volumeTorusCylinderDifference (radiusCylinder, width, height) {
      // Finds the volume of the substraction of concentric torus and cylinder

      // Radius of the such torus tube that intersects the cylinder
      // section at its corner points and has a certain width of a segment outside
      // of the intersection of the torus tube (circle) and cylinder section
      // (rectangular).
      const radiusTorusTube = height ** 2 / (8 * width) + width / 2
      // The distance from the center of the torus to the center of its tube
      const radiusTorus = radiusCylinder + width - radiusTorusTube
      // Central angle of torus tube from which the intersection segment is seen
      const phi = 2 * Math.asin((height / (2 * radiusTorusTube)).toPrecision(5))
      return (
        this.volumeCylinder(radiusTorus, height) +
        this.volumeCylinder(radiusTorusTube, height) -
        this.volumeCylinder(radiusCylinder, height) -
        Math.PI * (height ** 3) / 12 +
        Math.PI * (radiusTorusTube ** 2) * radiusTorus * (phi + Math.sin(phi))
      )
    }
  }
}
</script>

<style scoped lang="scss">
  .calculator-page {
    @include page-block-vertical-padding();
    & > .calculator-header {
      text-align: center;
      margin-bottom: 2em;
      & > h1 {
        @include text-block();
        margin-bottom: 0.5em;
      }
      & > p {
        @include text-block();
        font-family: "Baskerville Italic", $font-stack;
        line-height: normal;
        .wf-baskervilleitalic-n4-active & { font-size: 1.5em; }
      }
    }
    & > .calculator-section {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 2em;

      & > .left-panel, .right-panel {
        width: calc(50% - 2em);
        display: flex;
        flex-direction: column;
        & > * {
          margin-bottom: 1em;
        }
        & > *:last-child {
          margin-bottom: 0;
        }
      }
      & > .left-panel {
        padding-right: 1em;
        align-items: flex-end;
      }
      & > .right-panel {
        padding-left: 1em;
        align-items: flex-start;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/RingsCalculatorPage.vue